/**
 * Unified data source tracking for analytics across all entities.
 *
 * Use this type to track the origin/channel of data for:
 * - Users (registration source)
 * - Claims (how the claim was initiated)
 * - Redeems (redemption channel)
 * - Bookings (booking source)
 * - Purchases (transaction origin)
 *
 * ## HTTP Headers for SDK/API
 *
 * | Header              | Purpose                    | Examples                                              |
 * |---------------------|----------------------------|-------------------------------------------------------|
 * | `x-source-channel`  | How the request arrived    | `web`, `mobile`, `api`, `admin`, `webhook`, `import`  |
 * | `x-source-medium`   | Marketing channel type     | `email`, `referral`, `direct`, `organic`, `paid`      |
 * | `x-source-campaign` | Campaign ID for attribution| `summer_promo_2026`, `welcome_series`, `partner_launch`|
 * | `x-source`          | Traffic origin reference   | `google`, `facebook`, `partner_xyz`, `qr_code_lobby`  |
 *
 * ## Example Scenarios
 *
 * | Scenario                    | channel   | platform.deviceType | platform.app/browser | Notes |
 * |-----------------------------|-----------|---------------------|----------------------|-------|
 * | Desktop browser             | `web`     | `desktop`           | `browser: Chrome`    | -     |
 * | Mobile browser (Safari)     | `web`     | `phone`             | `browser: Safari`    | NOT 'mobile' channel |
 * | Native iOS app              | `mobile`  | `phone`             | `app: MyApp`         | Requires x-source-channel |
 * | Native Android app          | `mobile`  | `phone`             | `app: MyApp`         | Requires x-source-channel |
 * | Partner API integration     | `api`     | -                   | -                    | -     |
 * | Admin creates user          | `admin`   | -                   | -                    | -     |
 * | Webhook from Stripe         | `webhook` | -                   | -                    | -     |
 *
 * @example
 * // Simple usage - just channel
 * const source: DataSource = { channel: 'web' };
 *
 * // Full attribution tracking
 * const source: DataSource = {
 *   channel: 'web',
 *   medium: 'email',
 *   campaign: 'summer_promo_2026',
 *   source: 'mailchimp',
 *   referrer: 'https://email.example.com/campaign/123'
 * };
 */
/**
 * Primary ingestion channels for data source tracking.
 *
 * IMPORTANT:
 * - 'web' = ANY browser (desktop OR mobile) - use platform.deviceType to distinguish
 * - 'mobile' = Native apps ONLY (iOS/Android) - must include platform.app
 */
export const DATA_SOURCE_CHANNELS = [
    'web', // Web browser (desktop or mobile) - check platform.deviceType
    'mobile', // Native mobile app ONLY (iOS/Android) - requires platform.app
    'api', // Direct API integration
    'admin', // Admin panel/back-office
    'system', // System-generated (automated)
    'import', // Bulk import/CSV upload
    'webhook', // External webhook callback
    'sync', // Synchronization from external system
    'migration', // Data migration
    'unknown', // Source couldn't be determined (fallback)
];
/**
 * Type guard to check if a value is a valid DataSourceChannel.
 */
export function isDataSourceChannel(value) {
    return typeof value === 'string' && DATA_SOURCE_CHANNELS.includes(value);
}
/**
 * Default data source when source cannot be determined.
 * Used as fallback when CLS context is unavailable.
 *
 * NOTE: 'unknown' indicates a gap in tracking - should be rare.
 * If you see many 'unknown' sources, check that DataSourceInterceptor is active.
 */
export const DEFAULT_DATA_SOURCE = {
    channel: 'unknown',
};
/**
 * Known domain mappings for auto-detecting source from referrer.
 * Maps hostnames to normalized source names.
 */
export const KNOWN_SOURCE_DOMAINS = {
    // Google
    'google.com': 'google',
    'google.co.uk': 'google',
    'google.de': 'google',
    'google.fr': 'google',
    'google.es': 'google',
    'google.it': 'google',
    'google.nl': 'google',
    'google.pt': 'google',
    'google.pl': 'google',
    'google.ru': 'google',
    'google.com.br': 'google',
    'google.ca': 'google',
    'google.com.au': 'google',
    'google.co.jp': 'google',
    'google.co.in': 'google',
    // Facebook
    'facebook.com': 'facebook',
    'fb.com': 'facebook',
    'm.facebook.com': 'facebook',
    'l.facebook.com': 'facebook',
    'lm.facebook.com': 'facebook',
    // Instagram
    'instagram.com': 'instagram',
    'l.instagram.com': 'instagram',
    // Twitter/X
    'twitter.com': 'twitter',
    'x.com': 'twitter',
    't.co': 'twitter',
    'mobile.twitter.com': 'twitter',
    // LinkedIn
    'linkedin.com': 'linkedin',
    'lnkd.in': 'linkedin',
    // YouTube
    'youtube.com': 'youtube',
    'youtu.be': 'youtube',
    'm.youtube.com': 'youtube',
    // TikTok
    'tiktok.com': 'tiktok',
    'vm.tiktok.com': 'tiktok',
    // Other social
    'pinterest.com': 'pinterest',
    'reddit.com': 'reddit',
    'tumblr.com': 'tumblr',
    'snapchat.com': 'snapchat',
    // Search engines
    'bing.com': 'bing',
    'yahoo.com': 'yahoo',
    'duckduckgo.com': 'duckduckgo',
    'baidu.com': 'baidu',
    'yandex.com': 'yandex',
    'yandex.ru': 'yandex',
    // Email providers (for tracking email clicks)
    'mail.google.com': 'gmail',
    'outlook.live.com': 'outlook',
    'outlook.office.com': 'outlook',
};
/**
 * Auto-detect source from referrer URL domain.
 * Maps common domains to friendly source names.
 *
 * @param referrer - Full referrer URL
 * @returns Normalized source name or undefined
 *
 * @example
 * detectSourceFromReferrer('https://www.google.com/search?q=test') // 'google'
 * detectSourceFromReferrer('https://l.facebook.com/...') // 'facebook'
 * detectSourceFromReferrer('https://partner.example.com') // 'example'
 */
export function detectSourceFromReferrer(referrer) {
    if (!referrer)
        return undefined;
    try {
        const url = new URL(referrer);
        const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
        // Check exact match first
        if (KNOWN_SOURCE_DOMAINS[hostname]) {
            return KNOWN_SOURCE_DOMAINS[hostname];
        }
        // Check if hostname ends with known domain (for subdomains)
        for (const [domain, source] of Object.entries(KNOWN_SOURCE_DOMAINS)) {
            if (hostname.endsWith('.' + domain)) {
                return source;
            }
        }
        // Fallback: extract main domain name (e.g., 'example' from 'mail.example.com')
        const parts = hostname.split('.');
        if (parts.length >= 2) {
            return parts[parts.length - 2]; // Returns 'example' from 'sub.example.com'
        }
        return hostname;
    }
    catch {
        // Invalid URL, return undefined
        return undefined;
    }
}
/**
 * Parse User-Agent string for platform information (fallback when SDK doesn't provide).
 *
 * Extracts OS, browser, device type from standard User-Agent patterns.
 * This is best-effort - SDK headers are authoritative.
 *
 * @param userAgent - User-Agent header value
 * @returns Platform object with detected fields, or undefined if unparseable
 *
 * @example
 * parseUserAgentForPlatform('Mozilla/5.0 (iPhone; CPU iPhone OS 17_4...')
 * // { os: 'iOS', osVersion: '17.4', browser: 'Safari', deviceType: 'phone' }
 */
export function parseUserAgentForPlatform(userAgent) {
    if (!userAgent)
        return undefined;
    const ua = userAgent;
    const platform = {};
    // Detect OS and device type
    if (/iPhone|iPod/i.test(ua)) {
        platform.os = 'iOS';
        const match = ua.match(/OS (\d+)[_\.](\d+)/);
        if (match)
            platform.osVersion = `${match[1]}.${match[2]}`;
        platform.deviceType = 'phone';
    }
    else if (/iPad/i.test(ua)) {
        platform.os = 'iOS';
        const match = ua.match(/OS (\d+)[_\.](\d+)/);
        if (match)
            platform.osVersion = `${match[1]}.${match[2]}`;
        platform.deviceType = 'tablet';
    }
    else if (/Android/i.test(ua)) {
        platform.os = 'Android';
        const match = ua.match(/Android ([\d\.]+)/);
        if (match)
            platform.osVersion = match[1];
        platform.deviceType = /Mobile/i.test(ua) ? 'phone' : 'tablet';
    }
    else if (/Windows NT/i.test(ua)) {
        platform.os = 'Windows';
        const match = ua.match(/Windows NT ([\d\.]+)/);
        if (match)
            platform.osVersion = match[1];
        platform.deviceType = 'desktop';
    }
    else if (/Macintosh|Mac OS X/i.test(ua)) {
        platform.os = 'macOS';
        const match = ua.match(/Mac OS X ([\d_]+)/);
        if (match)
            platform.osVersion = match[1].replace(/_/g, '.');
        platform.deviceType = 'desktop';
    }
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) {
        platform.os = 'Linux';
        platform.deviceType = 'desktop';
    }
    // Detect browser (web channel)
    if (/Edg\//i.test(ua)) {
        platform.browser = 'Edge';
        const match = ua.match(/Edg\/([\d\.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/Chrome\//i.test(ua) && !/Edg/i.test(ua)) {
        platform.browser = 'Chrome';
        const match = ua.match(/Chrome\/([\d\.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) {
        platform.browser = 'Safari';
        const match = ua.match(/Version\/([\d\.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/Firefox\//i.test(ua)) {
        platform.browser = 'Firefox';
        const match = ua.match(/Firefox\/([\d\.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/Opera|OPR\//i.test(ua)) {
        platform.browser = 'Opera';
        const match = ua.match(/(?:Opera|OPR)\/([\d\.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    return Object.keys(platform).length > 0 ? platform : undefined;
}
/**
 * Auto-detect channel from User-Agent string.
 *
 * IMPORTANT DESIGN:
 * - Mobile browsers (Safari on iPhone, Chrome on Android) → 'web' (NOT 'mobile')
 * - Native apps MUST send explicit x-source-channel: mobile header
 * - Use platform.deviceType to distinguish mobile web from desktop web
 *
 * @param userAgent - User-Agent header value
 * @param path - Optional request path for webhook detection
 * @returns Detected channel
 *
 * @example
 * detectChannelFromUserAgent('Mozilla/5.0 (iPhone; ...) Safari') // 'web' (mobile browser)
 * detectChannelFromUserAgent('Mozilla/5.0 (Windows NT...)') // 'web' (desktop browser)
 * detectChannelFromUserAgent('', '/webhook/stripe') // 'webhook'
 * detectChannelFromUserAgent('MyApp/1.0') // 'unknown' (native app without header)
 */
export function detectChannelFromUserAgent(userAgent, path) {
    // Check for webhook paths
    if (path?.includes('/webhook')) {
        return 'webhook';
    }
    const ua = (userAgent || '').toLowerCase();
    // Check for bot/crawler patterns (system)
    if (/bot|crawler|spider|scraper|curl|wget|python|node-fetch|axios/i.test(ua)) {
        return 'api';
    }
    // Check for ANY browser pattern (desktop OR mobile)
    // Mobile browsers are 'web' channel - use platform.deviceType to distinguish
    if (ua && /mozilla|chrome|safari|firefox|edge|opera|msie|trident/i.test(ua)) {
        return 'web';
    }
    // Unknown: Native apps MUST send x-source-channel: mobile header
    // If we see 'unknown', it means SDK integration is incomplete
    return 'unknown';
}
/**
 * Parse UTM parameters from a URL (deep link, referrer, etc.).
 * Supports standard Google Analytics UTM parameters.
 *
 * @param url - Full URL containing UTM query parameters
 * @returns Parsed UTM parameters object
 *
 * @example
 * // Deep link from email campaign
 * parseUtmParams('myapp://screen?utm_source=mailchimp&utm_medium=email&utm_campaign=summer_2026')
 * // Returns: { source: 'mailchimp', medium: 'email', campaign: 'summer_2026' }
 *
 * @example
 * // Web URL with full UTM tracking
 * parseUtmParams('https://app.example.com?utm_source=google&utm_medium=cpc&utm_campaign=brand&utm_term=loyalty+app')
 * // Returns: { source: 'google', medium: 'cpc', campaign: 'brand', term: 'loyalty+app' }
 */
export function parseUtmParams(url) {
    const params = {};
    try {
        const urlObj = new URL(url);
        const searchParams = urlObj.searchParams;
        const source = searchParams.get('utm_source');
        const medium = searchParams.get('utm_medium');
        const campaign = searchParams.get('utm_campaign');
        const term = searchParams.get('utm_term');
        const content = searchParams.get('utm_content');
        if (source)
            params.source = source;
        if (medium)
            params.medium = medium;
        if (campaign)
            params.campaign = campaign;
        if (term)
            params.term = term;
        if (content)
            params.content = content;
    }
    catch {
        // Invalid URL, return empty params
    }
    return params;
}
//# sourceMappingURL=data-source.type.js.map